import { useState, useRef, useMemo } from 'react';
import { User, ClipboardList, File, FileText, Download, Upload, Trash2, X } from 'lucide-react';
import { StatusBadge } from '@/components/shared/StatusBadge';
import ModalShell from '@/components/shared/ModalShell';
import { formatCancerDisplay, formatTreatmentDisplay, hasStructuredOncology } from '@/components/shared/OncologyContext';
import { useLanguage } from '@/i18n/index';

/**
 * Onglet Dossier Médical complet du patient
 * Utilisé par NursePatientDetail et DoctorPatientDetail
 */

const DOC_TYPE_LABELS = {
  // Types backend canoniques
  ordonnance_medicament: "Ordonnance médicament",
  ordonnance_analyses: "Ordonnance analyses",
  ordonnance_imagerie: "Ordonnance imagerie",
  resultat_analyses: "Résultat analyses",
  resultat_imagerie: "Résultat imagerie",
  compte_rendu: "Compte-rendu médical",
  consentement: "Consentement",
  autre: "Autre",
  // Alias legacy (anciens uploads)
  medical_report: "Compte-rendu médical",
  lab_result: "Résultat d'analyse",
  prescription: "Ordonnance",
  imaging: "Imagerie",
  consent_form: "Consentement",
  other: "Autre",
  lab_order: "Ordonnance analyses",
  imaging_order: "Ordonnance imagerie",
};

// Phase 4-light — catégorisation visuelle par groupe (Tous / Ordonnances / etc.)
const DOC_CATEGORIES = [
  { key: "ALL", label: "Tous", types: null },
  {
    key: "ORDONNANCES",
    label: "Ordonnances",
    types: ["ordonnance_medicament", "ordonnance_analyses", "ordonnance_imagerie", "prescription", "lab_order", "imaging_order"],
  },
  {
    key: "BIO",
    label: "Résultats analyses",
    types: ["resultat_analyses", "lab_result"],
  },
  {
    key: "IMAGERIE",
    label: "Imagerie",
    types: ["resultat_imagerie", "imaging"],
  },
  {
    key: "CR",
    label: "Comptes-rendus",
    types: ["compte_rendu", "medical_report"],
  },
  {
    key: "AUTRE",
    label: "Autres",
    types: ["consentement", "consent_form", "autre", "other"],
  },
];

export default function PatientDossierTab({ 
  patient, 
  documents, 
  transmissions,
  onFileUpload,
  onDownload,
  onDeleteDoc,
  uploading,
  // Variant to customize displayed fields
  variant = "nurse" // "nurse" | "doctor"
}) {
  const { t } = useLanguage();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState("ordonnance_medicament");
  const [activeCategory, setActiveCategory] = useState("ALL");
  const fileInputRef = useRef(null);

  const docTypeOptions = [
    { value: "ordonnance_medicament", label: "Ordonnance médicament" },
    { value: "ordonnance_analyses", label: "Ordonnance analyses" },
    { value: "ordonnance_imagerie", label: "Ordonnance imagerie" },
    { value: "compte_rendu", label: "Compte-rendu médical" },
    { value: "consentement", label: "Consentement" },
    { value: "autre", label: "Autre" },
  ];

  // Phase 4-light — compteurs par catégorie (Tous, Ordonnances, Bio, Imagerie, CR, Autres)
  const categoryCounts = useMemo(() => {
    const counts = { ALL: documents.length };
    for (const cat of DOC_CATEGORIES) {
      if (!cat.types) continue;
      counts[cat.key] = documents.filter((d) => cat.types.includes(d.type)).length;
    }
    return counts;
  }, [documents]);

  const filteredDocs = useMemo(() => {
    if (activeCategory === "ALL") return documents;
    const cat = DOC_CATEGORIES.find((c) => c.key === activeCategory);
    if (!cat || !cat.types) return documents;
    return documents.filter((d) => cat.types.includes(d.type));
  }, [documents, activeCategory]);

  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      onFileUpload(e, selectedDocType);
      setShowUploadModal(false);
      setSelectedDocType("ordonnance_medicament");
    }
  };

  return (
    <div className="space-y-6">
      {/* Patient Info */}
      <div className="dash-card">
        <h3 className="font-semibold text-slate-800 mb-4">Informations patient</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-slate-500">Nom complet</p>
            <p className="font-medium text-slate-800">{patient.firstName} {patient.lastName}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Téléphone</p>
            <p className="font-medium text-slate-800">{patient.phone}</p>
          </div>
          
          {variant === "nurse" && (
            <>
              <div>
                <p className="text-xs text-slate-500">Date de naissance</p>
                <p className="font-medium text-slate-800">
                  {patient.dateOfBirth 
                    ? new Date(patient.dateOfBirth).toLocaleDateString("fr-FR")
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Taille</p>
                <p className="font-medium text-slate-800">
                  {patient.height ? `${patient.height} cm` : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Adresse</p>
                <p className="font-medium text-slate-800">{patient.address || "—"}</p>
              </div>
            </>
          )}
          
          <div>
            <p className="text-xs text-slate-500">Date de début suivi HANA</p>
            <p className="font-medium text-slate-800">
              {patient.startDate 
                ? new Date(patient.startDate).toLocaleDateString("fr-FR")
                : "—"}
            </p>
          </div>
          
          <div>
            <p className="text-xs text-slate-500">Date de début traitement</p>
            <p className="font-medium text-slate-800" data-testid="treatment-start-date">
              {patient.treatmentStartDate 
                ? new Date(patient.treatmentStartDate).toLocaleDateString("fr-FR")
                : "—"}
            </p>
          </div>
          
          {variant === "nurse" && (
            <div>
              <p className="text-xs text-slate-500">Médecin(s) référent(s)</p>
              {(patient.doctors && patient.doctors.length > 0)
                ? patient.doctors.map(d => (
                    <p key={d.id} className="font-medium text-slate-800">{d.name}</p>
                  ))
                : <p className="font-medium text-slate-800">{patient.doctorName || "Non assigné"}</p>
              }
            </div>
          )}
          
          {variant === "doctor" && (
            <div>
              <p className="text-xs text-slate-500">IDE référent(e)s</p>
              {(patient.nurses && patient.nurses.length > 0)
                ? patient.nurses.map(n => (
                    <p key={n.id} className="font-medium text-slate-800">{n.name}</p>
                  ))
                : <p className="font-medium text-slate-800">{patient.nurseName || "Non assigné"}</p>
              }
            </div>
          )}
          
          <div>
            <p className="text-xs text-slate-500">Statut actuel</p>
            <StatusBadge status={patient.status} />
          </div>
          <div>
            <p className="text-xs text-slate-500">Type de cancer</p>
            <p className="font-medium text-slate-800">{formatCancerDisplay(patient)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Traitement</p>
            <p className="font-medium text-slate-800">{formatTreatmentDisplay(patient)}</p>
          </div>
          {hasStructuredOncology(patient) && (
            <>
              <div>
                <p className="text-xs text-slate-500">Ligne thérapeutique</p>
                <p className="font-medium text-slate-800">{patient.therapeuticLine || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Protocole</p>
                <p className="font-medium text-blue-700">{patient.protocol || "—"}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Documents */}
      <div className="dash-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <File className="w-5 h-5 text-blue-600" />
            Documents médicaux ({documents.length})
          </h3>
          <button
            onClick={handleUploadClick}
            disabled={uploading}
            data-testid="upload-doc-btn"
            className="flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl px-4 py-2 text-sm font-medium hover:bg-emerald-100 transition-colors cursor-pointer disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Upload..." : "Ajouter"}
          </button>
        </div>

        {/* Upload Modal - Document Type Selection */}
        {showUploadModal && (
          <ModalShell testId="document-upload-modal" overlayClass="bg-black/40" containerClass="bg-white rounded-2xl">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-800">Ajouter un document</h4>
                <button onClick={() => setShowUploadModal(false)} className="text-slate-400 hover:text-slate-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-2">Type de document</label>
                <select
                  value={selectedDocType}
                  onChange={e => setSelectedDocType(e.target.value)}
                  data-testid="doc-type-select"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {docTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer">
                <Upload className="w-4 h-4" />
                Choisir le fichier
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                />
              </label>
              <p className="text-xs text-slate-400 text-center">PDF, Word, Excel, Images (max 10 Mo)</p>
            </div>
          </ModalShell>
        )}
        {documents.length === 0 ? (
          <p className="text-slate-400 text-center py-8">Aucun document</p>
        ) : (
          <>
            {/* Phase 4-light — Filtre par catégorie */}
            <div className="flex flex-wrap gap-1.5 mb-4 pb-3 border-b border-slate-100" data-testid="dossier-category-filter">
              {DOC_CATEGORIES.map((cat) => {
                const count = categoryCounts[cat.key] || 0;
                const active = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => setActiveCategory(cat.key)}
                    disabled={count === 0 && cat.key !== "ALL"}
                    data-testid={`dossier-category-${cat.key.toLowerCase()}`}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? "bg-blue-600 text-white border-blue-600"
                        : count === 0 && cat.key !== "ALL"
                        ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                        : "bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:text-blue-700"
                    }`}
                  >
                    {cat.label}
                    <span className={`ml-1.5 text-[10px] ${active ? "text-blue-100" : "text-slate-400"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {filteredDocs.length === 0 ? (
              <p className="text-slate-400 text-center py-8">Aucun document dans cette catégorie</p>
            ) : (
              <div className="space-y-2">
                {filteredDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`dossier-doc-${doc.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{doc.fileName}</p>
                        <p className="text-xs text-slate-500">
                          {DOC_TYPE_LABELS[doc.type] || doc.type} • {new Date(doc.createdAt).toLocaleDateString("fr-FR")}
                          {doc.uploaderRole && doc.uploaderRole !== "MEDECIN" && doc.uploaderRole !== "IDE" && (
                            <span className="ml-1.5 inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                              {doc.uploaderRole === "LABORATOIRE" ? "Labo"
                                : doc.uploaderRole === "SECRETAIRE_IMAGERIE" ? "Imagerie"
                                : doc.uploaderRole === "PHARMACIE" ? "Pharmacie"
                                : doc.uploaderRole}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onDownload(doc.id, doc.fileName)}
                        className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeleteDoc(doc.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Historique des transmissions */}
      <div className="dash-card">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-emerald-600" />
          Historique des transmissions
        </h3>
        {transmissions.length === 0 ? (
          <p className="text-slate-400 text-center py-8">Aucune transmission enregistrée</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {transmissions.map((tr, i) => (
              <div key={i} className={`p-4 rounded-lg border-l-4 ${
                tr.statusChange ? "bg-emerald-50 border-emerald-500" : "bg-slate-50 border-slate-300"
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">{tr.authorName || "Soignant"}</span>
                    <span className="text-xs text-slate-400">({tr.authorRole || "IDE"})</span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(tr.createdAt).toLocaleDateString("fr-FR", {
                      weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                    })}
                  </span>
                </div>
                {tr.statusChange && (
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                      Changement de statut
                    </span>
                    <span className="text-xs text-slate-600">
                      {t(`status.${tr.oldStatus}`)} → {t(`status.${tr.newStatus}`)}
                    </span>
                  </div>
                )}
                <p className="text-sm text-slate-700">{tr.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
